import { Listing, Category, Condition, TransactionType, ListingStyle, ListingMaterial, SpaceDetails, ListingStatus } from '../../../types';
import { ListingRow } from '../../../types/api';
import { getPublicR2FileUrl, getR2WorkerBaseUrl } from '../../storage-r2';
import { dedupeSpaceDetailLists } from '../../../utils/listingRow.mjs';

export class ListingMapper {
    private static safeJsonArray<T = any>(value: any, fallback: T[] = []): T[] {
        if (!value) return fallback;
        if (Array.isArray(value)) return value;
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? parsed : fallback;
            } catch (e) {
                console.warn('[ListingMapper] Failed to parse JSON array', e);
                return fallback;
            }
        }
        return fallback;
    }

    private static safeJson<T = any>(value: any, fallback?: T): T | undefined {
        if (!value) return fallback;
        if (typeof value === 'object') return value;
        if (typeof value === 'string') {
            try {
                return JSON.parse(value);
            } catch (e) {
                console.warn('[ListingMapper] Failed to parse JSON', e);
                return fallback;
            }
        }
        return fallback;
    }

    static toDomain(row: ListingRow): Listing {
        let parsedSpaceDetails: SpaceDetails | undefined = dedupeSpaceDetailLists(ListingMapper.safeJson<SpaceDetails>(row.space_details));

        let resolvedTransactionType = row.transaction_type ??
            (parsedSpaceDetails ? TransactionType.RENT : TransactionType.SELL);
            
        // Normalize 'rent' string to Enum label 'Prenájom'
        if (typeof resolvedTransactionType === 'string' && resolvedTransactionType.toLowerCase() === 'rent') {
            resolvedTransactionType = TransactionType.RENT;
        }

        const listing: Listing = {
            id: row.id,
            slug: row.slug || undefined,
            title: row.title || '',
            description: row.description || '',
            price: row.price || 0,
            currency: row.currency || 'EUR',
            category: row.category as Category,
            condition: row.condition as Condition,
            status: (row.status as ListingStatus) || ListingStatus.APPROVED,
            transactionType: resolvedTransactionType,
            style: row.listing_style as ListingStyle | undefined,
            material: row.material as ListingMaterial | undefined,
            location: row.location || '',
            zipCode: row.zip_code || undefined,
            images: ListingMapper.safeJsonArray(row.images),
            blurHashes: ListingMapper.safeJsonArray(row.blur_hashes),
            spaceDetails: parsedSpaceDetails,
            createdAt: row.created_at || new Date().toISOString().split('T')[0],
            seller: {
                id: row.seller_id || '',
                name: row.seller_name || '',
                email: row.seller_email || '',
                isVerified: !!row.seller_is_verified,
                rating: row.seller_rating || 0,
                ratingCount: row.seller_rating_count || 0,
                memberSince: row.seller_member_since || '',
                responseRate: row.seller_response_rate || 'N/A'
            },
            isFeatured: !!row.is_featured,
            views: row.views || 0,
            isAdminListing: !!row.is_admin_listing,
            trust: {
                source: (row as any).source ?? null,
                claimedByOwner: (row as any).claimed_by_owner == null ? null : !!Number((row as any).claimed_by_owner),
                verificationStatus: (row as any).verification_status ?? null,
                lastVerifiedAt: (row as any).last_verified_at ?? null,
                mediaLicenseStatus: (row as any).media_license_status ?? null,
            },
            management: row.managed_mode ? {
                managedMode: row.managed_mode === 1 || row.managed_mode === 'external' ? 'external' : 'internal',
                suppressInApp: !!row.managed_mode
            } : undefined,
            dimensions: ListingMapper.safeJson(row.dimensions)
        };

        // Fallback for coordinates
        const rowLat = row.lat || row.addr_lat;
        const rowLon = row.lon || row.addr_lon;

        if (listing.spaceDetails && (!listing.spaceDetails.address?.lat || !listing.spaceDetails.address?.lon)) {
            if (rowLat && rowLon) {
                if (!listing.spaceDetails.address) listing.spaceDetails.address = {};
                listing.spaceDetails.address.lat = Number(rowLat);
                listing.spaceDetails.address.lon = Number(rowLon);
            }
        } else if (!listing.spaceDetails && rowLat && rowLon) {
            listing.spaceDetails = {
                address: {
                    lat: Number(rowLat),
                    lon: Number(rowLon)
                }
            };
        }

        return this.sanitizeImages(listing);
    }

    static toRow(listing: Listing, sellerId?: string): any {
        const finalSellerId = sellerId || listing.seller?.id || 'unknown';
        const spaceDetailsJson = listing.spaceDetails ? JSON.stringify(listing.spaceDetails) : null;

        return {
            id: listing.id,
            slug: listing.slug || null,
            title: listing.title,
            description: listing.description || '',
            price: listing.price,
            currency: listing.currency || 'EUR',
            category: listing.category,
            condition: listing.condition,
            status: listing.status || ListingStatus.APPROVED,
            transaction_type: listing.transactionType,
            style: listing.style || null,
            material: listing.material || null,
            location: listing.location,
            zip_code: listing.zipCode || null,
            images: JSON.stringify(listing.images || []),
            blur_hashes: JSON.stringify(listing.blurHashes || []),
            space_details: spaceDetailsJson,
            seller_id: finalSellerId,
            seller_name: listing.seller.name,
            seller_email: listing.seller.email || '',
            seller_is_verified: listing.seller.isVerified ? 1 : 0,
            seller_rating: listing.seller.rating || 0,
            seller_rating_count: listing.seller.ratingCount || 0,
            seller_member_since: listing.seller.memberSince || '',
            seller_response_rate: listing.seller.responseRate || 'N/A',
            is_featured: listing.isFeatured ? 1 : 0,
            managed_mode: listing.management?.managedMode === 'external' ? 1 : 0,
            views: listing.views || 0,
            clicks: listing.clicks || 0,
            dimensions: listing.dimensions ? JSON.stringify(listing.dimensions) : null,
            created_at: listing.createdAt || new Date().toISOString(),
            updated_at: new Date().toISOString()
        };
    }

    /**
     * Preserves relative paths for database storage by stripping the R2 base URL.
     */
    static deSanitize(row: any): any {
        try {
            const base = getR2WorkerBaseUrl();
            if (!base) return row;

            const stripBase = (u: string) => {
                if (u.startsWith(base)) {
                    // e.g. "https://worker.dev/file/listing-images/path" -> "listing-images/path"
                    const filePrefix = '/file/';
                    const idx = u.indexOf(filePrefix);
                    if (idx !== -1) {
                        return u.slice(idx + filePrefix.length);
                    }
                }
                return u;
            };

            if (Array.isArray(row.images)) {
                row.images = row.images.map(stripBase);
            } else if (typeof row.images === 'string') {
                const parsed = ListingMapper.safeJsonArray(row.images);
                if (parsed.length > 0) {
                    row.images = JSON.stringify(parsed.map(stripBase));
                }
            }

            if (typeof row.space_details === 'string') {
                const d = ListingMapper.safeJson(row.space_details);
                if (d) {
                    if (d.logoUrl) d.logoUrl = stripBase(d.logoUrl);
                    row.space_details = JSON.stringify(d);
                }
            } else if (row.space_details && typeof row.space_details === 'object') {
                if (row.space_details.logoUrl) {
                    row.space_details.logoUrl = stripBase(row.space_details.logoUrl);
                }
            }

            return row;
        } catch (e) {
            console.error('[ListingMapper] deSanitize failed', e);
            return row;
        }
    }

    static sanitizeImages(listing: any): any {
        try {
            const images = Array.isArray(listing?.images) ? listing.images : [];
            const cleaned = images
                .filter((u: any) => typeof u === 'string' && u.trim().length > 0 && !u.startsWith('blob:') && !u.includes('.supabase.co'))
                .map((u: string) => {
                    if (/^(https?:)?\/\//.test(u) || u.startsWith('data:')) return u;
                    // If it starts with / it's a local asset (e.g. /external-gallery-placeholder.jpg) 
                    // should NOT be treated as something from R2
                    if (u.startsWith('/')) return u;
                    
                    const key = u.startsWith('listing-images/') ? u : (u.includes('/') ? `listing-images/${u}` : u);
                    return getPublicR2FileUrl(key) || u;
                });

            // Santize logoUrl if it exists in spaceDetails
            let spaceDetails = listing.spaceDetails;
            if (spaceDetails?.logoUrl) {
                const logo = spaceDetails.logoUrl;
                if (!logo.startsWith('http') && !logo.startsWith('data:') && !logo.startsWith('blob:')) {
                    const key = logo.startsWith('listing-images/') ? logo : (logo.includes('/') ? `listing-images/${logo}` : logo);
                    spaceDetails = {
                        ...spaceDetails,
                        logoUrl: getPublicR2FileUrl(key) || logo
                    };
                }
            }

            return { ...listing, images: cleaned, spaceDetails };
        } catch {
            return listing;
        }
    }
}
